import AsyncStorage from '@react-native-async-storage/async-storage';
import type {SessionBackend} from './fullSwingAuthSessionStorage';

export const ACTIVE_PLAYER_CONFIRMATION_STORAGE_KEY = 'ActivePlayerConfirmationTimestamp';
export const ACTIVE_PLAYER_CONFIRMATION_MAX_AGE_MS = 58_000;

export type ActivePlayerConfirmationOwner = {
  backend: SessionBackend, sessionId: string, userId: string, authSessionStart: string
};

export type PersistedActivePlayerConfirmation = ActivePlayerConfirmationOwner & {
  version: 5, timestamp: number
};

export type ActivePlayerConfirmationClearTarget = ActivePlayerConfirmationOwner & {
  timestamp?: number
};

// AsyncStorage offers no compare-and-delete primitive. Keep every operation on
// this recovery key in invocation order so stale cleanup cannot read an old
// record and then delete a newer prompt before removeItem runs.
let storageOperationQueue: Promise<void> = Promise.resolve();

const runStorageOperation = <T>(operation: () => Promise<T>): Promise<T> => {
  const result = storageOperationQueue.then(operation, operation);
  storageOperationQueue = result.then(() => undefined, () => undefined);
  return result;
};

const isSessionBackend = (value: unknown): value is SessionBackend => value === 'bay-session' || value === 'legacy';

const parseRecord = (raw: string): PersistedActivePlayerConfirmation | null => {
  try {
    const value = JSON.parse(raw) as Partial<PersistedActivePlayerConfirmation>;

    if (value.version !== 5 || !isSessionBackend(value.backend) || typeof value.sessionId !== 'string' || value.sessionId.length === 0 || typeof value.userId !== 'string' || value.userId.length === 0 || typeof value.authSessionStart !== 'string' || typeof value.timestamp !== 'number' || !Number.isFinite(value.timestamp) || value.timestamp <= 0) {
      return null;
    }

    return value as PersistedActivePlayerConfirmation;

  } catch {
    return null;
  }
};

export const activePlayerConfirmationOwnerEquals = (left: ActivePlayerConfirmationOwner, right: ActivePlayerConfirmationOwner): boolean => left.backend === right.backend && left.sessionId === right.sessionId && left.userId === right.userId && left.authSessionStart === right.authSessionStart;

export const readPersistedActivePlayerConfirmation = async (): Promise<PersistedActivePlayerConfirmation | null> => {
  return runStorageOperation(async () => {
    const raw = await AsyncStorage.getItem(ACTIVE_PLAYER_CONFIRMATION_STORAGE_KEY);
    if (!raw) return null;

    const record = parseRecord(raw);
    if (record) return record;

    // Unsupported records are never restored. This also purges the historic
    // raw timestamp and pre-v5 records that used a different ownership shape.
    await AsyncStorage.removeItem(ACTIVE_PLAYER_CONFIRMATION_STORAGE_KEY);
    return null;
  });
};

export const persistActivePlayerConfirmation = async (owner: ActivePlayerConfirmationOwner, timestamp: number): Promise<void> => {
  const record: PersistedActivePlayerConfirmation = {
    version: 5, backend: owner.backend, sessionId: owner.sessionId, userId: owner.userId,
    authSessionStart: owner.authSessionStart, timestamp
  };

  await runStorageOperation(() => AsyncStorage.setItem(ACTIVE_PLAYER_CONFIRMATION_STORAGE_KEY, JSON.stringify(record)));
};

export const clearPersistedActivePlayerConfirmation = async (expected?: ActivePlayerConfirmationClearTarget): Promise<void> => {
  await runStorageOperation(async () => {
    if (!expected) {
      await AsyncStorage.removeItem(ACTIVE_PLAYER_CONFIRMATION_STORAGE_KEY);
      return;
    }

    const raw = await AsyncStorage.getItem(ACTIVE_PLAYER_CONFIRMATION_STORAGE_KEY);
    if (!raw) return;

    const record = parseRecord(raw);
    const matchesExpectedRecord = record && activePlayerConfirmationOwnerEquals(record, expected) && (expected.timestamp === undefined || record.timestamp === expected.timestamp);

    if (!record || matchesExpectedRecord) {
      await AsyncStorage.removeItem(ACTIVE_PLAYER_CONFIRMATION_STORAGE_KEY);
    }
  });
};
